import { Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { CognitoService } from '../../core/services/cognito.service';
import { PermissionsService, PERMISSIONS } from '../../core/services/permissions.service';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';

@Component({
  selector: 'app-home-layout',
  imports: [RouterModule, CommonModule, HasPermissionDirective],
  templateUrl: './home-layout.component.html',
  styleUrl: './home-layout.component.css',
  standalone: true,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0', opacity: '0', overflow: 'hidden' }),
        animate('200ms ease-in-out', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        animate('200ms ease-in-out', style({ height: '0', opacity: '0' }))
      ])
    ])
  ]
})
export class HomeLayoutComponent {
  // Make permissions available in template
  PERMISSIONS = PERMISSIONS;
  
  sidebarVisible: boolean = false; // Sidebar is initially collapsed
  isMobile: boolean = false;
  isUserMenuOpen: boolean = false; // User menu dropdown state
  isNotificationsOpen: boolean = false; // Notifications dropdown state
  
  // Track open/closed state of submenus
  openSubmenus: { [key: string]: boolean } = {
    'reports': false,
    'settings': false,
    'security': false,
    'users': false,
    'admin': false
  };
  
  // Sample notifications for demonstration
  notifications: any = [];

  constructor(
    private router: Router, 
    private cognitoService: CognitoService,
    private permissionsService: PermissionsService
  ) { // Replace 'any' with your actual cognitoService type
    // Check if it's mobile on init
    this.checkScreenSize();
    
    // Listen for window resize events
    window.addEventListener('resize', () => this.checkScreenSize());

    // Add click event listener to close dropdown when clicking outside
    window.addEventListener('click', (e: Event) => {
      // Check if click is outside the user menu
      if (this.isUserMenuOpen && !(e.target as HTMLElement).closest('.user-menu-container')) {
        this.isUserMenuOpen = false;
      }
      
      // Check if click is outside the notifications panel
      if (this.isNotificationsOpen && !(e.target as HTMLElement).closest('.notifications-container')) {
        this.isNotificationsOpen = false;
      }
    });
  }
    // Check screen size and update isMobile flag
  checkScreenSize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768; // 768px is the md breakpoint in Tailwind
    
    // If changing from desktop to mobile, close sidebar
    if (!wasMobile && this.isMobile && this.sidebarVisible) {
      this.sidebarVisible = false;
    }
  }
    toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
    
    // Force a browser reflow to ensure animations are applied
    setTimeout(() => {
      // Force reflow to ensure the top position is applied immediately
      if (this.isMobile) {
        const sidebar = document.querySelector('.sidebar') as HTMLElement;
        if (sidebar) {
          sidebar.style.top = '4rem';
          sidebar.style.position = 'fixed';
          sidebar.style.zIndex = '9999';
        }
      }
    }, 10);
  }

  toggleUserMenu(event: Event): void {
    // Stop propagation to prevent the window click listener from immediately closing the menu
    event.stopPropagation();
    this.isUserMenuOpen = !this.isUserMenuOpen;
    
    // Close notifications if open
    if (this.isNotificationsOpen) {
      this.isNotificationsOpen = false;
    }
  }
  
  toggleNotifications(event: Event): void {
    // Stop propagation to prevent the window click listener from immediately closing the menu
    event.stopPropagation();
    this.isNotificationsOpen = !this.isNotificationsOpen;
    
    // Close user menu if open
    if (this.isUserMenuOpen) {
      this.isUserMenuOpen = false;
    }
  }
  
  markAsRead(notificationId: number): void {
    const notification = this.notifications.find((n: any) => n.id === notificationId);
    if (notification) {
      notification.read = true;
    }
  }
  
  markAllAsRead(): void {
    this.notifications.forEach((notification: any) => {
      notification.read = true;
    });
  }
  
  // Toggle submenu open/closed state
  toggleSubmenu(submenu: string): void {
    this.openSubmenus[submenu] = !this.openSubmenus[submenu];
  }
  
  // Check if a submenu is open
  isSubmenuOpen(submenu: string): boolean {
    return this.openSubmenus[submenu] === true;
  }
  
  getUnreadCount(): number {
    return this.notifications.filter((notification: any) => !notification.read).length;
  }

  logout(): void {
    this.cognitoService.signOut().then(() => {
      // Borrar del sesion storage los datos del usuario
      sessionStorage.removeItem('currentUser');
      localStorage.removeItem('userData');
      
      // Limpiar permisos
      this.permissionsService.clearPermissions();
      
      this.router.navigate(['/auth/login']);
    }).catch(error => {
      console.error('Error during logout:', error);
      // Handle error (e.g., show a notification)
    });
  }

  // Métodos para verificar permisos específicos del menú

  /**
   * Verificar si puede ver el panel de administración completo
   */
  canAccessAdminPanel(): boolean {
    return this.permissionsService.hasAnyPermission([
      PERMISSIONS.USER_READ,
      PERMISSIONS.ROLE_READ, 
      PERMISSIONS.BANKACCOUNT_READ,
      PERMISSIONS.ADMIN_SYSTEM,
      PERMISSIONS.ADMIN_TENANT
    ]);
  }

  /**
   * Verificar si puede ver gestión de usuarios
   */
  canAccessUserManagement(): boolean {
    return this.permissionsService.canReadUsers();
  }

  /**
   * Verificar si puede ver roles y permisos
   */
  canAccessRoles(): boolean {
    return this.permissionsService.canReadRoles();
  }

  /**
   * Verificar si puede ver cuentas bancarias
   */
  canAccessBankAccounts(): boolean {
    return this.permissionsService.canReadBankAccounts();
  }

  /**
   * Verificar si puede ver reglas de dispersión
   */
  canAccessDispersionRules(): boolean {
    return this.permissionsService.canReadDispersion();
  }

  /**
   * Verificar si puede ver logs y auditoría
   */
  canAccessAuditLogs(): boolean {
    return this.permissionsService.canReadAudit();
  }

  /**
   * Verificar si puede ver configuración del sistema
   */
  canAccessSettings(): boolean {
    return this.permissionsService.canAccessSettings();
  }

  /**
   * Verificar si es administrador (puede ver todo)
   */
  isAdmin(): boolean {
    return this.permissionsService.isAdmin();
  }
}
